import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { HashLink as Link } from "react-router-hash-link";
import { CAL_LINK } from "../../config/siteConfig";

/*
  ===== Copy Configuration =====
  (all editable text & structure lives here)
*/
const copy = {
    brandName: "Anindya Chowdhury",
    navItems: [
        { label: "Experience", to: "/#experience" },
        { label: "What I Solve", to: "/#what-i-solve" },
        { label: "Contact", to: "/#contact" },
    ],
    cta: { label: "Book a Conversation", href: CAL_LINK },
};

export default function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const toggleButtonRef = useRef(null);
    const mobileMenuRef = useRef(null);

    // Close mobile menu when route changes
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClick(e) {
            if (
                menuOpen &&
                mobileMenuRef.current &&
                !mobileMenuRef.current.contains(e.target) &&
                toggleButtonRef.current &&
                !toggleButtonRef.current.contains(e.target)
            ) {
                setMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [menuOpen]);

    // Close menu on Escape
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape") setMenuOpen(false);
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, []);

    // Add shadow when scrolled
    useEffect(() => {
        function handleScroll() {
            setIsScrolled(window.scrollY > 10);
        }
        window.addEventListener("scroll", handleScroll, { passive: true });
        // initialize on mount
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Helper to build a safe key for lists
    const makeKey = (prefix, label, idx) =>
        `${prefix}-${label?.toString().replace(/\s+/g, "-").toLowerCase() || idx}`;

    return (
        <header
            className={`sticky top-0 z-50 w-full py-0 px-2 transition-shadow duration-300 ${isScrolled ? "shadow-md" : ""
                }`}
            style={{ backgroundColor: "var(--bg-base)" }}
        >
            <div className="container mx-auto !px-2 lg:!px-12 !py-3 flex items-center justify-between">
                {/* Brand Name (Text) */}
                <Link
                    smooth
                    to="/#"
                    className="flex items-center text-lg md:text-xl font-bold tracking-tight text-[var(--text-primary)] hover:no-underline transition"
                    aria-label={copy.brandName}
                >
                    {copy.brandName}
                </Link>

                {/* Desktop Nav */}
                <nav
                    className="hidden md:flex items-center gap-7"
                    aria-label="Primary navigation"
                >
                    {copy.navItems.map((item, idx) => {
                        // Prefer `to` but fallback to `href`. If external (http/https) render <a>
                        const to = item.to || item.href || "#";
                        const isExternal = String(to).startsWith("http");
                        const key = makeKey("nav", item.label || to, idx);

                        if (isExternal) {
                            return (
                                <a
                                    key={key}
                                    href={to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:no-underline transition"
                                >
                                    {item.label}
                                </a>
                            );
                        }

                        return (
                            <Link
                                smooth
                                key={key}
                                to={to}
                                scroll={(el) => el.scrollIntoView({ behavior: "smooth", block: "start" })}
                                className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:no-underline transition"
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        );
                    })}

                    <a
                        href={CAL_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-[var(--color-primary)] text-[var(--bg-surface)] hover:opacity-90 rounded-full text-sm font-medium hover:no-underline transform transition shadow-sm"
                    >
                        {copy.cta.label}
                    </a>
                </nav>

                {/* Mobile Menu Button */}
                <button
                    ref={toggleButtonRef}
                    className="md:hidden bg-transparent focus:outline-none focus:ring-0"
                    onClick={() => setMenuOpen((o) => !o)}
                    aria-controls="mobile-menu"
                    aria-expanded={menuOpen}
                    aria-label={menuOpen ? "Close menu" : "Open menu"}
                >
                    <i className={`fa-solid ${menuOpen ? "fa-xmark" : "fa-bars"} text-2xl`} />
                </button>
            </div>

            {/* Mobile Dropdown */}
            {menuOpen && (
                <div
                    id="mobile-menu"
                    ref={mobileMenuRef}
                    className="md:hidden py-2 animate-slideDown"
                    style={{ backgroundColor: "var(--bg-base)" }}
                >
                    {copy.navItems.map((item, idx) => {
                        const to = item.to || item.href || "#";
                        const isExternal = String(to).startsWith("http");
                        const key = makeKey("mobile-nav", item.label || to, idx);

                        if (isExternal) {
                            return (
                                <a
                                    key={key}
                                    href={to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-gray-800 transition hover:font-semibold mx-4 py-3 border-b border-gray-200"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    {item.label}
                                </a>
                            );
                        }

                        return (
                            <Link
                                smooth
                                key={key}
                                to={to}
                                scroll={(el) => el.scrollIntoView({ behavior: "smooth", block: "start" })}
                                className="block text-gray-800 transition hover:font-semibold mx-4 py-3 border-b border-gray-200"
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        );
                    })}

                    <a
                        href={CAL_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mx-4 mt-3 bg-[var(--color-primary)] text-[var(--bg-surface)] hover:opacity-90 hover:no-underline py-2.5 px-4 rounded-full text-center text-sm font-medium transition shadow-sm"
                        onClick={() => setMenuOpen(false)}
                    >
                        {copy.cta.label}
                    </a>
                </div>
            )}
        </header>
    );
}
